import React from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import styled from "@emotion/styled";

import { ComboboxCollection, ComboboxItem, ComboboxList } from "components/ui/combobox";
import type { Option, OptionValue } from "components/ui/combobox/types";
import UIcon from "lib/icons/untitled_ui/UIcon";
import TooltipComponent from "../tooltip/Tooltip";

type InfiniteComboboxListProps = {
  items: Option[];
  filteredItems: Option[];
  value: OptionValue[];
  selectItem: (item: Option) => void;
  multiple: boolean;
  highlightedIndex: number;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage?: boolean;
  scrollAreaId: string;
  itemTooltip?: (option: Option) => React.ReactNode;
  children: (option: Option) => React.ReactNode;
};

export function InfiniteComboboxList({
  items,
  filteredItems,
  value,
  selectItem,
  multiple,
  highlightedIndex,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage = false,
  scrollAreaId,
  itemTooltip,
  children,
}: InfiniteComboboxListProps) {
  return (
    <ListScrollArea id={scrollAreaId}>
      <InfiniteScroll
        dataLength={filteredItems.length}
        next={fetchNextPage}
        hasMore={hasNextPage}
        loader={
          isFetchingNextPage ? (
            <LoadingContainer>
              <UIcon icon="spinner" />
            </LoadingContainer>
          ) : null
        }
        scrollableTarget={scrollAreaId}
        style={{ overflow: "inherit" }}
      >
        <ComboboxList items={items} filteredItems={filteredItems}>
          <ComboboxCollection options={filteredItems as Option[]}>
            {(option: Option) => {
              const base = (
                <ComboboxItem
                  key={String(option.value)}
                  option={option}
                  value={value}
                  selectItem={selectItem}
                  multiple={multiple}
                  highlightedIndex={highlightedIndex}
                  filteredItems={filteredItems}
                >
                  {children(option)}
                </ComboboxItem>
              );

              return itemTooltip ? (
                <TooltipComponent
                  key={String(option.value)}
                  side="left"
                  content={itemTooltip(option)}
                >
                  {base}
                </TooltipComponent>
              ) : (
                base
              );
            }}
          </ComboboxCollection>
        </ComboboxList>
      </InfiniteScroll>
    </ListScrollArea>
  );
}

const ListScrollArea = styled.div({
  maxHeight: 390,
  overflowY: "auto",
});

const LoadingContainer = styled.div({
  display: "flex",
  justifyContent: "center",
  padding: "8px",
  gap: 4,
});
